import React, { useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, ChevronDown, ChevronUp, Info } from "lucide-react";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "../GraderUI";
import { getScoreColorClass, getScoreHex } from "../gradeColors";

const PILLAR_ORDER = ["experience", "expertise", "authoritativeness", "trust"];

const PILLAR_LABELS = {
	experience: "Experience",
	expertise: "Expertise",
	authoritativeness: "Authoritativeness",
	trust: "Trust",
};

const PILLAR_DESCRIPTIONS = {
	experience: "First-hand proof: reviews, original media, \"how we made/tested it\"",
	expertise: "Named authors, bios, credentials, cited sources",
	authoritativeness: "Citations & brand recognition across AI and the web",
	trust: "Reviews, certifications, policies, contact & identity",
};

function getPillarScore(pillar) {
	if (pillar == null) return 0;
	if (typeof pillar === "number") return pillar;
	return Number(pillar.score ?? 0);
}

function getPillarWeight(pillar) {
	if (pillar == null || typeof pillar === "number") return 0;
	return Number(pillar.weight ?? 0);
}

function titleCaseWords(text) {
	if (!text) return "";
	return text
		.split(/\s+/)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");
}

function getScoreLevel(score) {
	if (score >= 80) return "strong";
	if (score >= 60) return "moderate";
	if (score >= 40) return "developing";
	return "weak";
}

function getPillarExtremes(pillars) {
	const entries = PILLAR_ORDER.map((key) => ({
		key,
		label: PILLAR_LABELS[key],
		score: getPillarScore(pillars[key]),
	}));
	const strongest = entries.reduce((a, b) => (b.score > a.score ? b : a));
	const weakest = entries.reduce((a, b) => (b.score < a.score ? b : a));
	return { strongest, weakest };
}

function AuthoritativenessMetrics({ components }) {
	if (!components) return null;

	const aiCitation = components.ai_citation;
	const brandEntity = components.brand_entity;
	const offsite = components.offsite_authority;
	const domainAuthority = components.domain_authority;
	const referringDomains = components.referring_domains;
	const backlinks = components.backlinks;

	const hasRow1 = aiCitation != null || brandEntity != null || offsite != null;
	const hasRow2 =
		domainAuthority != null || referringDomains != null || backlinks != null;

	if (!hasRow1 && !hasRow2) return null;

	return (
		<div className="eeat-score__auth-components">
			{hasRow1 && (
				<p className="eeat-score__auth-row">
					{aiCitation != null && (
						<span>
							AI Citation <strong>{Math.round(aiCitation)}</strong>
						</span>
					)}
					{brandEntity != null && (
						<span>
							Brand Recognition <strong>{Math.round(brandEntity)}</strong>
						</span>
					)}
					{offsite != null && (
						<span>
							Off-site <strong>{Math.round(offsite)}</strong>
						</span>
					)}
				</p>
			)}
			{hasRow2 && (
				<p className="eeat-score__auth-row eeat-score__auth-row--secondary">
					{[
						domainAuthority != null && (
							<>
								Domain authority <strong>{Math.round(domainAuthority)}</strong>
							</>
						),
						referringDomains != null && (
							<>
								<strong>{Number(referringDomains).toLocaleString()}</strong> referring
								domains
							</>
						),
						backlinks != null && (
							<>
								<strong>{Number(backlinks).toLocaleString()}</strong> backlinks
							</>
						),
					]
						.filter(Boolean)
						.map((part, i) => (
							<React.Fragment key={i}>
								{i > 0 ? ", " : ""}
								{part}
							</React.Fragment>
						))}
				</p>
			)}
		</div>
	);
}

function PillarCard({ pillarKey, pillar, index }) {
	const score = getPillarScore(pillar);
	const displayScore = Math.round(score);
	const barWidth = Math.min(Math.max(displayScore, 0), 100);
	const weight = getPillarWeight(pillar);
	const fillColor = getScoreHex(displayScore);

	return (
		<motion.div
			className="eeat-score__pillar-card"
			initial={{ opacity: 0, y: 12 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.35, delay: index * 0.08 }}
		>
			<div className="eeat-score__pillar-header">
				<div className="eeat-score__pillar-title-row">
					<h4 className="eeat-score__pillar-title">{PILLAR_LABELS[pillarKey]}</h4>
					<span className="eeat-score__pillar-weight">{Math.round(weight)}%</span>
				</div>
				<span className={`eeat-score__pillar-score ${getScoreColorClass(displayScore)}`}>
					{displayScore}
				</span>
			</div>

			<div className="eeat-score__pillar-bar">
				<motion.div
					className="eeat-score__pillar-bar-fill"
					initial={{ width: 0 }}
					animate={{ width: `${barWidth}%` }}
					transition={{ duration: 0.7, delay: index * 0.08 + 0.2 }}
					style={{ background: fillColor }}
				/>
			</div>

			<p className="eeat-score__pillar-description">{PILLAR_DESCRIPTIONS[pillarKey]}</p>

			{pillarKey === "authoritativeness" && (
				<AuthoritativenessMetrics components={pillar?.components} />
			)}
		</motion.div>
	);
}

export default function EeatScoreSection({ report }) {
	const [explainerOpen, setExplainerOpen] = useState(true);

	if (!report?.pillars) return null;

	const overallScore = Math.round(Number(report.overall_score ?? 0));
	const scoreLevel = getScoreLevel(overallScore);
	const { strongest, weakest } = getPillarExtremes(report.pillars);
	const gaps = report.gaps || {};
	const allGaps = [
		...(gaps.experience || []),
		...(gaps.expertise || []),
		...(gaps.trust || []),
	];

	return (
		<motion.div
			className="report-section eeat-score"
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.5 }}
		>
			<div className="eeat-score__top">
				<div className="report-section__header eeat-score__header">
					<div className="report-section__icon-wrap report-section__icon-wrap--purple">
						<ShieldCheck className="report-section__icon" />
					</div>
					<div className="report-section__header-text">
						<h2 className="report-section__title eeat-score__title">
							E-E-A-T Score
							<Info className="eeat-score__title-icon" aria-hidden />
						</h2>
						<p className="report-section__subtitle">
							Experience · Expertise · Authoritativeness · Trust — how credible AI
							judges your brand
						</p>
					</div>
				</div>

				<div className="eeat-score__consolidated">
					<div className="eeat-score__consolidated-row">
						<span
							className={`eeat-score__consolidated-value ${getScoreColorClass(overallScore)}`}
						>
							{overallScore}
						</span>
						<span className="eeat-score__consolidated-max">/ 100</span>
					</div>
					<span className="eeat-score__consolidated-label">consolidated</span>
				</div>
			</div>

			<Collapsible open={explainerOpen} onOpenChange={setExplainerOpen}>
				<CollapsibleTrigger className="eeat-score__explainer-trigger">
					How this score works
					{explainerOpen ? (
						<ChevronUp className="eeat-score__chevron" aria-hidden />
					) : (
						<ChevronDown className="eeat-score__chevron" aria-hidden />
					)}
				</CollapsibleTrigger>
				<CollapsibleContent>
					<div className="eeat-score__explainer-box">
						<p>
							<strong>How it&apos;s computed:</strong> A 0–100 blend of four pillars:{" "}
							Trust <strong>35%</strong> (trust signals + reviews, certifications &amp;
							policies), Authoritativeness <strong>30%</strong> (AI Citation + Brand
							Recognition), Expertise <strong>20%</strong> (named authors, bios,
							credentials, cited sources), Experience <strong>15%</strong> (first-hand
							language, reviews, case studies, original media). It replaces the separate
							Brand Recognition, AI Citation and Trust line items in the GEO score (no
							double-counting).
						</p>
						<p>
							<strong>Why it matters:</strong> E-E-A-T is how AI assistants decide
							whether your brand is credible enough to cite and recommend. Stronger
							E-E-A-T means you&apos;re surfaced and named more often in AI answers.
						</p>
						<p>
							<strong>Current score analysis:</strong> Your consolidated E-E-A-T is{" "}
							<strong>
								{overallScore}/100 ({scoreLevel})
							</strong>
							. Strongest pillar:{" "}
							<strong>
								{strongest.label} ({Math.round(strongest.score)})
							</strong>
							. Needs the most work:{" "}
							<strong>
								{weakest.label} ({Math.round(weakest.score)})
							</strong>
							. Start with the gaps listed below and the matching GEO recommendations.
						</p>
					</div>
				</CollapsibleContent>
			</Collapsible>

			<div className="eeat-score__pillar-grid">
				{PILLAR_ORDER.map((key, index) => (
					<PillarCard
						key={key}
						pillarKey={key}
						pillar={report.pillars[key]}
						index={index}
					/>
				))}
			</div>

			{allGaps.length > 0 && (
				<div className="report-section__callout report-section__callout--purple eeat-score__gaps">
					<h3 className="report-section__callout-title eeat-score__gaps-title">
						<Info className="eeat-score__gaps-icon" aria-hidden />
						Biggest E-E-A-T gaps
					</h3>
					<ul className="eeat-score__gaps-list">
						{allGaps.map((gap, i) => (
							<li key={i}>{titleCaseWords(gap)}</li>
						))}
					</ul>
					<p className="eeat-score__gaps-footer">
						See the GEO recommendations below for plain-language fixes with examples.
					</p>
				</div>
			)}
		</motion.div>
	);
}
